import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LuLayoutDashboard, 
  LuUsers, 
  LuCreditCard, 
  LuFileText, 
  LuShieldCheck, 
  LuUser, 
  LuLogOut,
  LuFiles
} from 'react-icons/lu';

const HeaderBar = styled.header`
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  background: ${props => props.theme.colors.gradientPrimary};
  color: ${props => props.theme.colors.white};
  padding: 20px 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  
  /* O html já reserva safe-area-inset-top. No desktop o header soma um respiro extra. */
  padding-top: calc(max(20px, env(safe-area-inset-top, 24px)) + 20px);

  @media (max-width: 768px) {
    padding-top: 12px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  position: relative;
  min-height: 110px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    min-height: 0;
  }
`;

const Brand = styled(Link)`
  font-size: 24px;
  font-weight: 700;
  text-decoration: none;
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  
  img {
    width: 110px;
    height: 110px;
    display: block;
    object-fit: contain;
    transition: transform 0.3s ease;
    box-sizing: none;
    
    &:hover {
      transform: scale(1.05);
    }

    &:focus-visible{
      outline: none;
    }
  }
  
  @media (max-width: 768px) {
    img {
      width: 72px;
      height: 72px;
    }
  }
`;

const ContactInfo = styled.div`
  font-size: 14px;
  text-align: right;
  line-height: 1.4;
  
  strong { 
    font-weight: 700; 
  }
  
  div {
    margin-bottom: 2px;
  }
  
  @media (max-width: 768px) {
    text-align: center;
    order: 3;
  }
`;

const ContractLink = styled(Link)`
  color: inherit; /* Herda a cor do texto pai */
  text-decoration: underline; /* Adiciona sublinhado para indicar que é clicável */
  cursor: pointer;
  transition: color 0.3s ease;
  margin-right: 0px;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    color: ${props => props.theme.colors.textLight}; /* Muda a cor ao passar o mouse */
    text-decoration: none; /* Remove o sublinhado ao passar o mouse (opcional) */
  }

  &:focus {
    outline: none;
  }
`;

const NavContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 24px;
  border-radius: 50px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  a { 
    color: white; 
    font-weight: 500; 
    opacity: 0.9;
    text-decoration: none;
    transition: all 0.3s ease;
    padding: 8px 16px;
    border-radius: 20px;
    position: relative;
    
    &:hover {
      opacity: 1;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      transform: translateY(-2px);
      text-decoration: none;
    }
  }
  
  a.active { 
    opacity: 1;
    background: rgba(255, 255, 255, 0.25);
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 10px 16px;
    
    a {
      padding: 6px 12px;
      font-size: 14px;
    }
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding: 8px 12px;
    
    a {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
`;

const Header = () => {
  const { logout, user, contratos, selectedContrato } = useAuth(); // Obtém os dados do usuário do AuthContext
  const location = useLocation();
  const isActive = (path) => location.pathname === path ? 'active' : '';
  
  return (
    <HeaderBar>
      <Container>
        <HeaderContent>
          <Brand to="/dashboard" aria-label="Santa Casa Copacabana">
            <img src="/logo2.png" alt="Santa Casa Copacabana" />
          </Brand>
          <ContactInfo>
            {user &&  (
              <>
                <div>Bem-vindo(a), <strong>{user.nome || 'Usuário(a)'}</strong></div>
                {contratos.length === 1 && (
                  <div>
                    <ContractLink to="/">{selectedContrato?.plano} ({selectedContrato?.numero || 'N/A'})</ContractLink>
                  </div>
                )}
                {contratos.length > 1 && (
                  <div>
                    <ContractLink to="/contracts">{selectedContrato?.plano} ({selectedContrato?.numero || 'N/A'})</ContractLink>
                  </div>
                )}
              </>
            )}
          </ContactInfo>
          
        </HeaderContent>

        <NavContainer>
          <Nav>
            {contratos.length > 1 && (
              <Link className={isActive('/contracts')} to="/contracts">
                <LuFiles size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Meus Contratos
              </Link>
            )}
            <Link className={isActive('/dashboard')} to="/dashboard">
              <LuLayoutDashboard size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Dashboard
            </Link>
            <Link className={isActive('/dependents')} to="/dependents">
              <LuUsers size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Dependentes
            </Link>
            <Link className={isActive('/payments')} to="/payments">
              <LuCreditCard size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Pagamentos
            </Link>
            <Link className={isActive('/solicitacoes')} to="/solicitacoes">
              <LuFileText size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Solicitações
            </Link>
            <Link className={isActive('/coverage')} to="/coverage">
              <LuShieldCheck size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Cobertura
            </Link>
            <Link className={isActive('/profile')} to="/profile">
              <LuUser size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Perfil
            </Link>
            <a href="#sair" onClick={(e) => { e.preventDefault(); logout(); }}>
              <LuLogOut size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Sair
            </a>
          </Nav>
        </NavContainer>
      </Container>
    </HeaderBar>
  );
};

export default Header;
